import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Fab,
  Zoom,
  Tooltip,
  Divider,
  Fade
} from '@mui/material';
import {
  Forum,
  Close,
  Send,
  SmartToy,
  SupportAgent,
  Person,
  Refresh,
  Lock
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';

// Define message structure
interface SupportMessage {
  id: string;
  senderType: 'USER' | 'AI' | 'ADMIN';
  senderName: string;
  content: string;
  createdAt: string;
}

export const SupportChatWidget: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<'ACTIVE_AI' | 'ACTIVE_HUMAN' | 'CLOSED' | null>(null);
  
  // Registration form for guests
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  
  // CAPTCHA details
  const [captchaNum1, setCaptchaNum1] = useState(0);
  const [captchaNum2, setCaptchaNum2] = useState(0);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);

  // Messages and Input
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAiTyping, setIsAiTyping] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const BACKEND_URL = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com'
    : (process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001');

  const API_URL = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

  // Initialize/retrieve existing session
  useEffect(() => {
    const savedSessionId = localStorage.getItem('rc_support_session_id');
    if (savedSessionId) {
      resumeSession(savedSessionId);
    }
    generateCaptcha();
  }, []);

  // Regenerate CAPTCHA when registration values reset
  const generateCaptcha = () => {
    const n1 = Math.floor(Math.random() * 9) + 1;
    const n2 = Math.floor(Math.random() * 9) + 1;
    setCaptchaNum1(n1);
    setCaptchaNum2(n2);
    setCaptchaAnswer('');
    setCaptchaVerified(false);
    setCaptchaError(false);
  };

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAiTyping]);

  // Connect to Socket.io when session details change
  useEffect(() => {
    if (!sessionId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    console.log('🔌 Connecting Support Socket.io. Session:', sessionId);
    const token = localStorage.getItem('rideclub_token');

    const socket = io(BACKEND_URL, {
      auth: {
        isSupport: true,
        supportSessionId: sessionId,
        token: token || undefined
      },
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      console.log('✅ Support socket connected:', socket.id);
      socket.emit('support:join', sessionId);
    });

    // Listen for incoming messages from Admin takeover
    socket.on('support:message:receive', (message: any) => {
      console.log('📩 Support message received via socket:', message);
      // Avoid duplicates
      setMessages(prev => {
        if (prev.some(m => m.id === message.id)) return prev;
        return [...prev, {
          id: message.id,
          senderType: message.senderType,
          senderName: message.senderName,
          content: message.content,
          createdAt: message.createdAt
        }];
      });
      // Admin messages switch the status/view to ACTIVE_HUMAN
      if (message.senderType === 'ADMIN') {
        setSessionStatus('ACTIVE_HUMAN');
        setIsAiTyping(false);
      }
    });

    // Listen for session closed event
    socket.on('support:session:closed', (data: any) => {
      console.log('🔒 Support session closed via socket');
      setSessionStatus('CLOSED');
      if (data?.message) {
        setMessages(prev => {
          if (prev.some(m => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
      }
    });

    socketRef.current = socket;

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [sessionId]);

  // Authenticated users: Auto-start session if none exists
  useEffect(() => {
    if (user && !sessionId && isOpen) {
      autoStartAuthSession();
    }
  }, [user, isOpen]);

  const resumeSession = async (sessId: string) => {
    try {
      const response = await axios.get(`${API_URL}/support/session/${sessId}/messages`);
      if (response.data.success) {
        setSessionId(sessId);
        setMessages(response.data.data.messages || []);
        setSessionStatus(response.data.data.status);
      } else {
        localStorage.removeItem('rc_support_session_id');
      }
    } catch (err) {
      console.error('Failed to resume support session:', err);
      localStorage.removeItem('rc_support_session_id');
    }
  };

  const autoStartAuthSession = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('rideclub_token');
      const response = await axios.post(
        `${API_URL}/support/session`,
        {},
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        }
      );

      if (response.data.success) {
        const newSessionId = response.data.sessionId;
        setSessionId(newSessionId);
        setSessionStatus(response.data.session.status);
        localStorage.setItem('rc_support_session_id', newSessionId);
        
        // Initial AI greeting
        setMessages([
          {
            id: 'init-msg',
            senderType: 'AI',
            senderName: 'Ride Club AI',
            content: `Hello ${user?.preferredName || user?.firstName || 'there'}! I am the Ride Club AI Support Assistant. How can I help you today?`,
            createdAt: new Date().toISOString()
          }
        ]);
      }
    } catch (err) {
      console.error('Failed to start authenticated support session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const startGuestSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim() || !guestEmail.trim()) return;

    // Verify Captcha
    const expected = captchaNum1 + captchaNum2;
    if (parseInt(captchaAnswer) !== expected) {
      setCaptchaError(true);
      return;
    }

    setCaptchaVerified(true);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/support/session`, {
        guestName: guestName.trim(),
        guestEmail: guestEmail.trim()
      });

      if (response.data.success) {
        const newSessionId = response.data.sessionId;
        setSessionId(newSessionId);
        setSessionStatus(response.data.session.status);
        localStorage.setItem('rc_support_session_id', newSessionId);

        setMessages([
          {
            id: 'init-msg',
            senderType: 'AI',
            senderName: 'Ride Club AI',
            content: `Hi ${guestName.trim()}! Thanks for reaching out. I'm your Ride Club AI Assistant. I can help answer questions about payouts, ride postings, credits, and rules. How can I assist you today?`,
            createdAt: new Date().toISOString()
          }
        ]);
      }
    } catch (err) {
      console.error('Failed to start guest support session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || !sessionId || sessionStatus === 'CLOSED') return;

    const currentInput = inputText.trim();
    setInputText('');

    // Pre-insert user message visually for responsiveness
    const tempUserMsg: SupportMessage = {
      id: `temp-${Date.now()}`,
      senderType: 'USER',
      senderName: user ? `${user.firstName} ${user.lastName}` : guestName || 'Guest',
      content: currentInput,
      createdAt: new Date().toISOString()
    };

    setMessages(prev => [...prev, tempUserMsg]);

    if (sessionStatus === 'ACTIVE_AI') {
      setIsAiTyping(true);
    }

    try {
      const token = localStorage.getItem('rideclub_token');
      const response = await axios.post(
        `${API_URL}/support/session/${sessionId}/message`,
        { content: currentInput },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        }
      );

      if (response.data.success) {
        // Replace temp user message with actual DB saved message
        setMessages(prev => prev.map(m => m.id === tempUserMsg.id ? response.data.userMessage : m));
        
        // If there is an AI response, add it
        if (response.data.aiMessage) {
          setMessages(prev => [...prev, response.data.aiMessage]);
        }
        
        // Update status (e.g. escalated to ACTIVE_HUMAN)
        setSessionStatus(response.data.status);
      }
    } catch (err: any) {
      console.error('Failed to send message:', err);
      // Remove temp message and show error message
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      
      const errorText = err.response?.data?.message || 'Failed to send message. Please try again.';
      setMessages(prev => [...prev, {
        id: `err-${Date.now()}`,
        senderType: 'AI',
        senderName: 'System',
        content: `⚠️ Error: ${errorText}`,
        createdAt: new Date().toISOString()
      }]);
    } finally {
      setIsAiTyping(false);
    }
  };

  const handleClearSession = () => {
    localStorage.removeItem('rc_support_session_id');
    setSessionId(null);
    setSessionStatus(null);
    setMessages([]);
    setGuestName('');
    setGuestEmail('');
    generateCaptcha();
  };

  const guestMessagesCount = messages.filter(m => m.senderType === 'USER').length;
  const isGuest = !user;
  const characterLimit = isGuest ? 500 : 1000;
  const isNearLimit = isGuest && guestMessagesCount >= 12;

  return (
    <>
      {/* Floating Action Button */}
      <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
        <Zoom in={true}>
          <Tooltip title="Help & Chat Support" placement="left">
            <Fab
              color="primary"
              onClick={() => setIsOpen(!isOpen)}
              sx={{
                width: 60,
                height: 60,
                background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
                boxShadow: '0 8px 32px rgba(46, 125, 50, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                '&:hover': {
                  transform: 'scale(1.1) rotate(5deg)',
                  boxShadow: '0 12px 40px rgba(46, 125, 50, 0.45)',
                }
              }}
            >
              {isOpen ? <Close sx={{ fontSize: 28 }} /> : <Forum sx={{ fontSize: 28 }} />}
            </Fab>
          </Tooltip>
        </Zoom>
      </Box>

      {/* Floating Chat Drawer Container */}
      <Fade in={isOpen}>
        <Paper
          elevation={16}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            width: { xs: 'calc(100% - 48px)', sm: 342 },
            height: { xs: 'calc(100vh - 140px)', sm: 495 },
            maxHeight: 585,
            borderRadius: '20px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.15)',
          }}
        >
          {/* Chat Header */}
          <Box
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #1E4620 0%, #2E7D32 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  width: 44,
                  height: 44,
                  border: '2px solid rgba(255,255,255,0.4)'
                }}
              >
                {sessionStatus === 'ACTIVE_HUMAN' ? (
                  <SupportAgent sx={{ fontSize: 26 }} />
                ) : (
                  <SmartToy sx={{ fontSize: 26 }} />
                )}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {sessionStatus === 'ACTIVE_HUMAN' ? 'Ride Club Live Agent' : 'Ride Club Assistant'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#4CAF50',
                      animation: 'pulse 1.5s infinite'
                    }}
                  />
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    {sessionStatus === 'ACTIVE_HUMAN' ? 'Live takeover active' : 'AI Support Online'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {sessionId && (
                <Tooltip title="Reset Conversation">
                  <IconButton size="small" onClick={handleClearSession} sx={{ color: 'white', opacity: 0.7, '&:hover': { opacity: 1 } }}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white', opacity: 0.7, '&:hover': { opacity: 1 } }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Chat Body */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column' }}>
            {!sessionId ? (
              /* Session Form for guests */
              <Box
                component="form"
                onSubmit={startGuestSession}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  my: 'auto',
                  p: 1.5,
                  textAlign: 'center'
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="bold" color="text.primary" gutterBottom>
                    Need Help?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Provide your name and email to start chatting with our instant AI assistant.
                  </Typography>
                </Box>

                <TextField
                  label="Your Name"
                  size="small"
                  variant="outlined"
                  required
                  value={guestName}
                  onChange={e => setGuestName(e.target.value)}
                  fullWidth
                  sx={{ bgcolor: 'white' }}
                />

                <TextField
                  label="Email Address"
                  type="email"
                  size="small"
                  variant="outlined"
                  required
                  value={guestEmail}
                  onChange={e => setGuestEmail(e.target.value)}
                  fullWidth
                  sx={{ bgcolor: 'white' }}
                />

                {/* Bot Protection CAPTCHA */}
                <Box
                  sx={{
                    p: 2,
                    border: '1px dashed #cbd5e1',
                    borderRadius: '12px',
                    bgcolor: '#f1f5f9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Lock sx={{ fontSize: 16, color: '#64748b' }} />
                    <Typography variant="caption" fontWeight="bold" color="text.secondary">
                      Spam Check
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    What is {captchaNum1} + {captchaNum2}?
                  </Typography>
                  <TextField
                    placeholder="Enter answer"
                    size="small"
                    variant="outlined"
                    type="number"
                    required
                    value={captchaAnswer}
                    onChange={e => setCaptchaAnswer(e.target.value)}
                    error={captchaError}
                    helperText={captchaError ? 'Incorrect answer' : ''}
                    sx={{ width: 140, mt: 0.5, '& input': { textAlign: 'center', bgcolor: 'white' } }}
                  />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  sx={{
                    mt: 1,
                    py: 1.2,
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
                    borderRadius: '10px',
                    boxShadow: '0 4px 15px rgba(46, 125, 50, 0.2)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1E4620 0%, #2E7D32 100%)',
                    }
                  }}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Start Chat'}
                </Button>
              </Box>
            ) : (
              /* Message Display Log */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {messages.map((msg, index) => {
                  const isUser = msg.senderType === 'USER';
                  const isSystem = msg.senderName === 'System';
                  
                  if (isSystem) {
                    return (
                      <Box key={msg.id || index} sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            px: 2,
                            py: 0.75,
                            borderRadius: '10px',
                            bgcolor: '#f1f5f9',
                            border: '1px solid #e2e8f0',
                            textAlign: 'center',
                            maxWidth: '90%'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" fontWeight="medium">
                            {msg.content}
                          </Typography>
                        </Paper>
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={msg.id || index}
                      sx={{
                        display: 'flex',
                        justifyContent: isUser ? 'flex-end' : 'flex-start',
                        gap: 1
                      }}
                    >
                      {!isUser && (
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: msg.senderType === 'ADMIN' ? '#1E4620' : '#4CAF50',
                            color: 'white',
                            fontSize: 14
                          }}
                        >
                          {msg.senderType === 'ADMIN' ? 'A' : 'AI'}
                        </Avatar>
                      )}
                      
                      <Box sx={{ maxWidth: '75%' }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            ml: isUser ? 0 : 1,
                            mr: isUser ? 1 : 0,
                            display: 'block',
                            textAlign: isUser ? 'right' : 'left'
                          }}
                        >
                          {msg.senderName}
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            bgcolor: isUser ? '#E8F5E9' : 'white',
                            color: 'text.primary',
                            border: '1px solid',
                            borderColor: isUser ? '#C8E6C9' : '#E2E8F0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                            '& p': { m: 0, '&:not(:last-child)': { mb: 1 } },
                            '& ul, & ol': { m: 0, pl: 2 }
                          }}
                        >
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                      </Box>
                    </Box>
                  );
                })}

                {/* AI / Agent Typing Indicator */}
                {isAiTyping && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#4CAF50', color: 'white' }}>AI</Avatar>
                    <Box sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: '16px 16px 16px 4px' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Box sx={{ width: 6, height: 6, bgcolor: '#94a3b8', borderRadius: '50%', animation: 'bounce 1.4s infinite alternate' }} />
                        <Box sx={{ width: 6, height: 6, bgcolor: '#94a3b8', borderRadius: '50%', animation: 'bounce 1.4s infinite alternate', animationDelay: '0.2s' }} />
                        <Box sx={{ width: 6, height: 6, bgcolor: '#94a3b8', borderRadius: '50%', animation: 'bounce 1.4s infinite alternate', animationDelay: '0.4s' }} />
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Warnings (Message counts for guests) */}
                {isNearLimit && (
                  <Box sx={{ p: 1.5, bgcolor: '#FFF3E0', border: '1px solid #FFE0B2', borderRadius: '10px', textAlign: 'center' }}>
                    <Typography variant="caption" color="warning.dark" display="block">
                      💡 <strong>Guest Session Warning:</strong> You have used {guestMessagesCount}/15 guest messages. Please sign up or log in to keep full support history.
                    </Typography>
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Box>
            )}
          </Box>

          {/* Chat Footer Input */}
          {sessionId && (
            <Box
              component="form"
              onSubmit={handleSendMessage}
              sx={{
                p: 1.5,
                bgcolor: 'white',
                borderTop: '1px solid #E2E8F0',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  placeholder={sessionStatus === 'CLOSED' ? 'This conversation is closed' : 'Type a message...'}
                  disabled={sessionStatus === 'CLOSED' || isAiTyping}
                  size="small"
                  value={inputText}
                  onChange={e => {
                    if (e.target.value.length <= characterLimit) {
                      setInputText(e.target.value);
                    }
                  }}
                  fullWidth
                  variant="outlined"
                  autoComplete="off"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '24px',
                      bgcolor: '#F8FAFC',
                      pr: 1
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontSize: 10 }}>
                        {inputText.length}/{characterLimit}
                      </Typography>
                    )
                  }}
                />
                <IconButton
                  type="submit"
                  disabled={!inputText.trim() || sessionStatus === 'CLOSED' || isAiTyping}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#f1f5f9',
                      color: '#cbd5e1'
                    }
                  }}
                >
                  <Send sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          )}
        </Paper>
      </Fade>

      {/* Embedded Animations and Keyframes */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes pulse {
          0% { transform: scale(0.95); opacity: 0.5; }
          50% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(0.95); opacity: 0.5; }
        }
        @keyframes bounce {
          0% { transform: translateY(0); }
          100% { transform: translateY(-4px); }
        }
      `}} />
    </>
  );
};

export default SupportChatWidget;
